import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { TrendingUp, Users, Globe, Shield, ArrowRight, Download } from 'lucide-react';
import { investorMetrics, turbineModels, testimonials } from '../mock';

const Investors = () => {
  const navigate = useNavigate();

  return (
    <div className="investors-page">
      {/* Header */}
      <section className="page-header investors-header">
        <div className="container">
          <h1 className="page-title">Investment Opportunity</h1>
          <p className="page-subtitle">
            Profitable Distributed Energy Infrastructure at Global Scale
          </p>
          <div className="header-metrics">
            <div className="header-metric">
              <span className="metric-number">€22M-€106M</span>
              <span className="metric-label">Profit per 1,000 units</span>
            </div>
            <div className="header-metric">
              <span className="metric-number">759M</span>
              <span className="metric-label">People without electricity</span>
            </div>
            <div className="header-metric">
              <span className="metric-number">4-6 years</span>
              <span className="metric-label">Payback period</span>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="thesis-section">
        <div className="container">
          <div className="thesis-grid">
            <div className="thesis-content">
              <h2 className="section-title">The Opportunity</h2>
              <p className="thesis-text">
                The global energy crisis presents a massive market opportunity. 759 million people 
                lack access to electricity, energy costs are rising globally, and grid infrastructure 
                remains unstable in many regions. Diesel dependence continues to drive pollution 
                and operational costs.
              </p>
              <p className="thesis-text">
                Our single-blade carbon fibre VAWT with cloud-optimised algorithms delivers 
                profitable small-scale distributed energy that no competitor can match.
              </p>
              
              <div className="thesis-highlights">
                <div className="highlight-item">
                  <TrendingUp className="highlight-icon" />
                  <div>
                    <h4>Superior Unit Economics</h4>
                    <p>40% efficiency, 4-6 year payback, 100-year structure lifetime</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <Users className="highlight-icon" />
                  <div>
                    <h4>Owner-Operator Model</h4>
                    <p>Fund purchases turbines, recurring cash flow from energy sales</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <Globe className="highlight-icon" />
                  <div>
                    <h4>Global Scalability</h4>
                    <p>Regional manufacturing hubs, licensed operating companies</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <Shield className="highlight-icon" />
                  <div>
                    <h4>IP Defensibility</h4>
                    <p>Encrypted software, hardware-locked optimization, trade secrets</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="thesis-visual">
              <img 
                src="https://images.unsplash.com/photo-1508791290064-c27cc1ef7a9a" 
                alt="Wind Energy Infrastructure"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Unit Economics */}
      <section className="economics-section">
        <div className="container">
          <h2 className="section-title">Unit Economics</h2>
          <div className="economics-grid">
            {turbineModels.map(model => {
              const metrics = investorMetrics.unitEconomics[model.id === 'firefly-4kw' ? 'firefly' : 'firebird'];
              return (
                <Card key={model.id} className="economics-card">
                  <div className="card-header">
                    <h3>{model.name}</h3>
                    <div className="card-badge">{model.power}kW</div>
                  </div>
                  
                  <div className="economics-metrics">
                    <div className="econ-row">
                      <span className="econ-label">Installed Cost</span>
                      <span className="econ-value">€{metrics.installedCost.toLocaleString()}</span>
                    </div>
                    <div className="econ-row">
                      <span className="econ-label">Annual Revenue</span>
                      <span className="econ-value">€{metrics.annualRevenue.toLocaleString()}</span>
                    </div>
                    <div className="econ-row highlight">
                      <span className="econ-label">Payback Period</span>
                      <span className="econ-value success">{metrics.paybackYears} years</span>
                    </div>
                    <div className="econ-row highlight">
                      <span className="econ-label">10-Year Net Cash</span>
                      <span className="econ-value success">€{metrics.tenYearNetCash.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="profitability-badge">
                    <span>Highly Profitable</span>
                  </div>
                </Card>
              );
            })}
          </div>
          
          <Card className="scale-economics">
            <h3>Scale Economics: Profit per 1,000 Units</h3>
            <div className="scale-metrics">
              <div className="scale-item">
                <span className="scale-label">Conservative (FireFly)</span>
                <span className="scale-value">€{(investorMetrics.profitPerThousandUnits.low / 1000000).toFixed(1)}M</span>
              </div>
              <div className="scale-item">
                <span className="scale-label">Aggressive (FireBird)</span>
                <span className="scale-value success">€{(investorMetrics.profitPerThousandUnits.high / 1000000).toFixed(1)}M</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Roadmap */}
      <section className="roadmap-section">
        <div className="container">
          <h2 className="section-title">Growth Roadmap</h2>
          <div className="roadmap-timeline">
            {investorMetrics.roadmap.map((phase, idx) => (
              <div key={idx} className="roadmap-item">
                <div className="roadmap-marker">{idx + 1}</div>
                <Card className="roadmap-card">
                  <h3 className="roadmap-phase">{phase.phase}</h3>
                  <div className="roadmap-target">{phase.target}</div>
                  <p className="roadmap-focus">{phase.focus}</p>
                  <div className="roadmap-timeline-label">{phase.timeline}</div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantage */}
      <section className="competitive-section">
        <div className="container">
          <h2 className="section-title">Competitive Landscape</h2>
          <div className="competitive-grid">
            <Card className="competitor-card">
              <h3>Traditional HAWT</h3>
              <ul className="competitor-list negative">
                <li>High servicing costs</li>
                <li>Noise pollution</li>
                <li>Low small-scale efficiency</li>
                <li>Complex maintenance</li>
              </ul>
            </Card>
            
            <Card className="competitor-card">
              <h3>Diesel Generators</h3>
              <ul className="competitor-list negative">
                <li>Fuel dependency</li>
                <li>Rising operational costs</li>
                <li>Environmental pollution</li>
                <li>No sustainability</li>
              </ul>
            </Card>
            
            <Card className="competitor-card">
              <h3>Solar-Only Solutions</h3>
              <ul className="competitor-list negative">
                <li>Night downtime</li>
                <li>Battery dependency</li>
                <li>Space requirements</li>
                <li>Weather variability</li>
              </ul>
            </Card>
            
            <Card className="competitor-card highlight">
              <h3>Turbo Algorithms</h3>
              <ul className="competitor-list positive">
                <li>Hybrid wind + solar</li>
                <li>Cloud-intelligent</li>
                <li>Highly profitable</li>
                <li>Minimal maintenance</li>
                <li>24/7 generation</li>
                <li>Encrypted & secure</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">Industry Recognition</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="testimonial-card">
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.avatar}</div>
                  <div className="author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                    <div className="author-company">{testimonial.company}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Investor FAQ</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            <AccordionItem value="item-1">
              <AccordionTrigger>What is the total addressable market (TAM)?</AccordionTrigger>
              <AccordionContent>
                The global distributed energy market is massive. 759 million people lack electricity access, 
                representing a multi-billion dollar opportunity. Additionally, millions of commercial, industrial, 
                and agricultural operations seek to reduce energy costs and carbon footprint through on-site 
                renewable generation.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2">
              <AccordionTrigger>What are the capital requirements?</AccordionTrigger>
              <AccordionContent>
                Seed Round: €400k for proof of concept (10 units) and initial market validation.
                Series A: €4M for manufacturing scale-up, cloud platform expansion, and sales partnerships.
                Funds will be allocated to regional manufacturing hubs, technology development, and go-to-market execution.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3">
              <AccordionTrigger>How is intellectual property protected?</AccordionTrigger>
              <AccordionContent>
                Our IP strategy includes: (1) Encrypted software with hardware-locked optimization that prevents 
                reverse engineering, (2) Trade-secret architecture for blade design and TSR algorithms, 
                (3) Distributed license model for regional operators, (4) Continuous innovation in materials 
                and control systems to maintain technological leadership.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4">
              <AccordionTrigger>What is the go-to-market strategy?</AccordionTrigger>
              <AccordionContent>
                Phase 1: Direct sales to early adopters and pilot deployments (10 units). Phase 2: Partner with 
                regional distributors and energy cooperatives (100 units). Phase 3: Establish infrastructure 
                funds and licensed operating companies for large-scale deployments (1,000+ units). The owner-operator 
                model creates recurring revenue streams from energy sales.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5">
              <AccordionTrigger>What are the key risks and mitigation strategies?</AccordionTrigger>
              <AccordionContent>
                Key risks include: (1) Manufacturing scale-up - mitigated through partnerships with established 
                carbon fibre suppliers, (2) Market adoption - addressed via strong unit economics and pilot programs, 
                (3) Technology competition - maintained through continuous R&D and IP protection, (4) Regulatory 
                changes - diversified across multiple markets and jurisdictions.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="investor-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Join the Renewable Energy Revolution</h2>
            <p>Request our full investor deck and financial projections</p>
            <div className="cta-buttons">
              <Button size="lg" className="cta-primary">
                <Download className="mr-2" />
                Download Investor Deck
              </Button>
              <Button size="lg" variant="outline" className="cta-secondary" onClick={() => navigate('/calculator')}>
                View Financial Calculator <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investors;
